import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BedDouble, CheckCircle2, Loader2, Plane, PlaneTakeoff } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { IMG } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "h-11 rounded-xl border-slate-200 bg-white";

const TABS = [
  { id: "flight", label: "Flight Booking Request", icon: PlaneTakeoff },
  { id: "hotel", label: "Hotel Booking Request", icon: BedDouble },
  { id: "combo", label: "Flight + Hotel Package", icon: Plane },
];

export default function FlightsHotels() {
  const [tab, setTab] = useState("flight");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({
    name: "", phone: "", email: "",
    from: "", to: "", depart: "", ret: "", cabin: "Economy",
    destination: "", checkin: "", checkout: "", rooms: "1 Room",
    adults: 2, children: 0, notes: "",
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const lines = [];
    if (tab !== "hotel") lines.push(`Flight: ${f.from} → ${f.to} | Depart ${f.depart || "flexible"}${f.ret ? ` | Return ${f.ret}` : ""} | Cabin: ${f.cabin}`);
    if (tab !== "flight") lines.push(`Hotel: ${f.destination} | Check-in ${f.checkin || "flexible"} → Check-out ${f.checkout || "flexible"} | ${f.rooms}`);
    if (f.notes) lines.push(`Notes: ${f.notes}`);
    try {
      await axios.post(`${API}/enquiries`, {
        name: f.name, phone: f.phone, email: f.email,
        destination: f.to || f.destination,
        travel_date: f.depart || f.checkin,
        adults: Number(f.adults) || 0, children: Number(f.children) || 0,
        trip_type: "Flights & Hotels",
        message: lines.join("\n"),
        package_name: TABS.find((t) => t.id === tab).label,
        source: "flights-hotels",
      });
      setDone(true);
    } catch {
      toast.error("Could not submit. Please try WhatsApp or call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="flights-hotels-page">
      <SEO title="Flights & Hotels" description="Flight booking requests, hotel reservations and flight + hotel packages — send a request and D Travel Link reverts with the best options and fares." />
      <PageHero image={IMG.plane1} overline="End-to-End Booking" title="Flights & Hotels"
                description="No fake inventory here — tell us your route and dates, and our team reverts with real, bookable options and honest fares." />
      <section className="py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap gap-2" data-testid="fh-tabs">
              {TABS.map((t) => (
                <button key={t.id} onClick={() => { setTab(t.id); setDone(false); }} data-testid={`fh-tab-${t.id}`}
                        className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === t.id ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-royal hover:text-royal"}`}>
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-8">
            <div className="rounded-2xl bg-white border border-slate-200/80 p-6 sm:p-8 shadow-sm">
              {done ? (
                <div className="text-center py-8" data-testid="fh-success">
                  <CheckCircle2 className="w-12 h-12 text-skybrand mx-auto" />
                  <h3 className="mt-4 font-heading text-2xl font-bold text-slate-900">Request received!</h3>
                  <p className="mt-2 text-sm text-slate-500 max-w-sm mx-auto">Our team will revert with options and fares within 2 business hours.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="fh-form">
                  {tab !== "hotel" && (
                    <>
                      <div><Label>From *</Label><Input required data-testid="fh-from" placeholder="e.g. Mumbai (BOM)" value={f.from} onChange={set("from")} className={inputCls} /></div>
                      <div><Label>To *</Label><Input required data-testid="fh-to" placeholder="e.g. Singapore (SIN)" value={f.to} onChange={set("to")} className={inputCls} /></div>
                      <div><Label>Departure Date</Label><Input type="date" data-testid="fh-depart" value={f.depart} onChange={set("depart")} className={inputCls} /></div>
                      <div><Label>Return Date</Label><Input type="date" data-testid="fh-return" value={f.ret} onChange={set("ret")} className={inputCls} /></div>
                      <div>
                        <Label>Cabin</Label>
                        <select data-testid="fh-cabin" value={f.cabin} onChange={set("cabin")} className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm">
                          {["Economy", "Premium Economy", "Business", "First"].map((c) => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                    </>
                  )}
                  {tab !== "flight" && (
                    <>
                      <div><Label>Hotel Destination *</Label><Input required data-testid="fh-hotel-destination" placeholder="e.g. Bali — Seminyak" value={f.destination} onChange={set("destination")} className={inputCls} /></div>
                      <div><Label>Check-in</Label><Input type="date" data-testid="fh-checkin" value={f.checkin} onChange={set("checkin")} className={inputCls} /></div>
                      <div><Label>Check-out</Label><Input type="date" data-testid="fh-checkout" value={f.checkout} onChange={set("checkout")} className={inputCls} /></div>
                      <div>
                        <Label>Room Requirements</Label>
                        <select data-testid="fh-rooms" value={f.rooms} onChange={set("rooms")} className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm">
                          {["1 Room", "2 Rooms", "3+ Rooms", "Family / Connecting Rooms"].map((r) => <option key={r}>{r}</option>)}
                        </select>
                      </div>
                    </>
                  )}
                  <div><Label>Adults</Label><Input type="number" min={1} data-testid="fh-adults" value={f.adults} onChange={set("adults")} className={inputCls} /></div>
                  <div><Label>Children</Label><Input type="number" min={0} data-testid="fh-children" value={f.children} onChange={set("children")} className={inputCls} /></div>
                  <div><Label>Your Name *</Label><Input required data-testid="fh-name" value={f.name} onChange={set("name")} className={inputCls} /></div>
                  <div><Label>Mobile Number *</Label><Input required type="tel" minLength={8} data-testid="fh-phone" value={f.phone} onChange={set("phone")} className={inputCls} /></div>
                  <div className="sm:col-span-2"><Label>Email *</Label><Input required type="email" data-testid="fh-email" value={f.email} onChange={set("email")} className={inputCls} /></div>
                  <div className="sm:col-span-2"><Label>Anything else?</Label><Input data-testid="fh-notes" placeholder="Preferred airline, hotel star category, budget..." value={f.notes} onChange={set("notes")} className={inputCls} /></div>
                  <Button type="submit" disabled={submitting} data-testid="fh-submit"
                          className="sm:col-span-2 h-12 rounded-full bg-navy text-white font-heading font-bold hover:bg-royal transition-colors">
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "SUBMIT REQUEST"}
                  </Button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
